<template>
  <div class="gallery">
    <!-- Thumbnails -->
    <div
      class="thumbnail-container"
      ref="thumbnails"
      @mouseover="startScrolling"
      @mouseout="stopScrolling"
      @mousemove="scrollThumbnails"
      v-if="images.length > 1"
    >
      <div ref="thumbnail" v-for="(image, index) in images" :key="index" :class="{'thumbnail-active': index === active}" class="thumbnail" @click="showImage(index)">
        <img v-lazy-load :width="image.Sizes[0]" :height="image.Sizes[1]" :data-src="image.Resized[0]" alt="Thumbnail" :src="require('@/assets/images/placeholder.png')"/>
      </div>
    </div>
    <!-- arrows -->
    <a class="fullscreen-images-pre" v-if="images.length > 1" @click="prev()"><span>&larr;</span></a>
    <a class="fullscreen-images-nxt" v-if="images.length > 1" @click="next()"><span>&rarr;</span></a>

    <!-- Fullscreen Image -->
    <div class="fullscreen-images" ref="container">
        <div class="fullscreen-image" v-for="(image, index) in images" :key="`l-${index}`"
          v-observe-visibility="{callback: imagesObserver, intersection: { threshold: 0.25}}"
          :data-index="index"
          ref="fullscreenImages"
        >
          <div class="wrapper">
            <div class="wrapper-aspect" :style="`aspect-ratio: ${image.Sizes[0]} / ${image.Sizes[1]};`">
              <img
                :data-src="image.Resized[2]"
                :width="image.Sizes[0]"
                :height="image.Sizes[1]"
                :data-srcset="`${image.Resized[0]} 640w, ${image.Resized[1]} 1280w, ${image.Resized[2]} 2560w`"
              />
            </div>
            <div v-if="image.Captions[0]" class="caption">
              <p v-html="$root.md(image.Captions[0])"/>
            </div>
          </div>
        </div>
    </div>
  </div>
</template>

<script>
import { lazyLoadDirective } from '@/directives/lazyLoad'
import Vue from 'vue'
import VueObserveVisibility from 'vue-observe-visibility'
import { scroller } from 'vue-scrollto/src/scrollTo'
Vue.use(VueObserveVisibility)

export default {
  directives: {
    'lazy-load': lazyLoadDirective
  },
  props: {
    images: Array
  },
  computed: {
    fullscreenImage () {
      return this.images[this.active]
    }
  },
  data () {
    return {
      active: 0,
      scrolling: false,
      scrubbing: false,
      scrollTo: scroller(),
      scrollToThumbs: scroller(),
      scrollSpeed: 1, // Adjust as needed
      scrollDirection: 1 // 1 for right, -1 for left
    }
  },
  mounted () {
  },
  methods: {
    prev () {
      this.showImage(this.active > 0 ? this.active - 1 : this.images.length - 1)
    },
    next () {
      this.showImage(this.active < this.images.length - 1 ? this.active + 1 : 0)
    },
    startScrolling () {
      this.scrubbing = true
    },
    stopScrolling () {
      this.scrubbing = false
    },
    scrollThumbnails (event) {
      if (this.scrubbing) {
        const thumbnails = this.$refs.thumbnails
        const mouseX = event.clientX - thumbnails.getBoundingClientRect().left
        const scrollPercent = mouseX / thumbnails.offsetWidth
        this.scrollOffset = scrollPercent * (thumbnails.scrollWidth - thumbnails.offsetWidth)
        thumbnails.scrollLeft = this.scrollOffset
      }
    },
    imagesObserver (isVisible, entry) {
      if (entry.isIntersecting === true && !this.scrolling) {
        this.active = +entry.target.dataset.index
        this.setActive(entry.target)

        const _container = this.$refs.thumbnails
        const _target = this.$refs.thumbnail[this.active]
        this.scrollToThumbs(_target, 250, {
          container: _container,
          x: true,
          y: false,
          cancelable: false
        })
      }
    },
    setActive (entry) {
      const img = entry.querySelector('img')
      img.src = img.dataset.src
      img.srcset = img.dataset.srcset
      img.classList.add('visible')
    },
    showImage (index) {
      if (this.scrolling) return
      this.active = index
      const _container = this.$refs.container
      const _target = this.$refs.fullscreenImages[this.active]
      this.setActive(_target)
      _container.style.setProperty('scroll-snap-type', 'none')
      this.scrolling = true
      this.scrollTo(_target, 250, {
        container: _container,
        x: true,
        y: false,
        cancelable: false,
        onDone: (e) => {
          _container.style.removeProperty('scroll-snap-type')
          this.scrolling = false
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
@import '../main';
.gallery {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: var(--padding-20);
  margin-bottom: var(--padding-20);
  @include sm() {
    padding-top: 0;
  }
  position: relative;
  .thumbnail-container {
    overflow: hidden;
    width: 100%;
    text-align: center;
    white-space: nowrap;
    .thumbnail {
      margin: 2px;
      cursor: pointer;
      opacity: 0.5;
      display: inline-block;
      img {
        width: auto;
        height: 10vh;
        filter: grayscale(100%);
        background: #666;
      }
      &-active {
        opacity: 1
      }
    }
    @include sm() {
      display: none;
    }
  }
  .fullscreen-images {
    overflow-x: auto;
    width: 100%;
    white-space: nowrap;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    scroll-snap-type: x mandatory;
    margin-top: var(--padding);
    ::-webkit-scrollbar {
        display: none; /* Older WebKit browsers */
    }
    scrollbar-width: none;
    position: relative;
    &-pre,
    &-nxt {
      cursor: pointer;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      @include headerfont;
      z-index: 100;
      height: 100%;
      width: 3rem;
      display: none;
      justify-content: center;
      align-items: center;
      @include sm() {
        display: flex;
      }
      span {
        color: black;
        background: white;
        height: 1rem;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        width: 1.5rem;
        height: 1.5rem;
        opacity: 0.75;
      }
    }
    &-pre {
      left: 0rem;
    }
    &-nxt {
      right: 0rem;
    }
    @include sd() {
      max-width: var(--paperwidth);
    }
  }

  .fullscreen-image {
    width: calc(100vw - 4 * var(--menuwidth-hidden));
    @include sd() {
      max-width: var(--paperwidth);
    }
    @include sm() {
      width: calc(100vw - 2 * var(--menuwidth-hidden) - 3 * var(--padding));
      box-sizing: border-box;
    }
    box-sizing: border-box;
    display: inline-block;
    scroll-snap-align: start;
    .wrapper {
      margin: auto;
      &-aspect {
        position: relative;
        margin: auto;
        width: 100%;
        max-height: 66vh;
      }
      img {
        position: absolute;
        width: auto;
        height: 100%;
        left: 50%;
        transform: translateX(-50%);
        opacity: 0;
        transition: opacity 1.25s;
        &.visible {
          opacity: 1
        }
      }
      .caption {
        p {
          @include smallfont;
          margin-top: var(--padding-05);
          margin-bottom: 0;
          padding-bottom: 0;
          white-space: normal;
          max-width: var(--paperwidth);
          margin-left: auto;
          margin-right: auto;
        }
      }
    }

  }
}
</style>



// WEBPACK FOOTER //
// src/components/GalleryDefault.vue